// Stage Mail-1:単一 Mail アカウントの作成・編集フォーム。
//
// 仕様(design §3.7):
// - 名前 / 邮箱地址
// - IMAP:host / port(default 993) / security / username / password
// - SMTP:host / port(default 587) / security / username / password
// - 各 password は SecretField、autoComplete=off
// - 保存ボタンを押すと onSave を呼ぶ(onSave 内で IMAP / SMTP password 入り body を作成)
// - 連携解除済の編集:password 必須 + 既存 is_active(false) を維持
// - 親(MailSettingsCard)が state を持ち、submit / cancel を制御する

import React, { useState } from "react";
import { Save, RefreshCw, Mail, Server } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { SecretField } from "../../components/settings/SecretField";
import type {
  MailAccount,
  MailSecurity,
  MailAccountCreateBody,
  MailSecurityBlock,
  MailTestResult,
} from "../../api/mail";
import { ApiError } from "../../api/client";
import {
  PROVIDER_LABELS,
  PROVIDER_ORDER,
  detectProviderFromEmail,
  presetToFormValues,
  type ProviderKey,
} from "../../api/mailProviders";

export interface MailAccountFormProps {
  /** 編集中の account。不在なら新規作成。 */
  account?: MailAccount | null;
  /** 保存時呼び出し。親が DTO を受け取り、API 呼び出しは親が行う。 */
  onSave: (body: MailAccountCreateBody) => Promise<void> | void;
  /** キャンセル。 */
  onCancel: () => void;
  /** 接続テスト。mode=input|stored で credential を返す / DB を使う。 */
  onTestImap: (body:
    | { mode: "input"; config: MailSecurityBlock }
    | { mode: "stored" }
  ) => Promise<MailTestResult>;
  onTestSmtp: (body:
    | { mode: "input"; config: MailSecurityBlock }
    | { mode: "stored" }
  ) => Promise<MailTestResult>;
  /** busy 状態(create / update / delete 等)。 */
  busy?: "save" | "test-imap" | "test-smtp" | null;
  allowedAccountTypes?: readonly AccountType[];
}

const DEFAULT_IMAP_PORT = 993;
const DEFAULT_SMTP_PORT = 587;
const ACCOUNT_TYPES = ["personal", "shared", "other"] as const;
type AccountType = typeof ACCOUNT_TYPES[number];
type TestResultPanel = {
  result: MailTestResult;
};

export function MailAccountForm({
  account,
  onSave,
  onCancel,
  onTestImap,
  onTestSmtp,
  busy = null,
  allowedAccountTypes = ACCOUNT_TYPES,
}: MailAccountFormProps) {
  const isEditing = !!account;
  const isDisconnected = isEditing && !account!.is_active;

  const [name, setName] = useState(account?.name ?? "");
  const [emailAddress, setEmailAddress] = useState(account?.email_address ?? "");
  const [isActive, setIsActive] = useState(account?.is_active ?? true);
  const [accountType, setAccountType] = useState<AccountType>(account?.account_type ?? "personal");
  // Mail-V2.5 (2026-08-19): メールプロバイダー preset。新規作成時のみ UI に出すが
  // editing モードでも internal state 保持(account 編集時に host を 上書きしないため)。
  const [providerKey, setProviderKey] = useState<ProviderKey>("other");

  // IMAP / SMTP auth uses the mail address as username and this single shared password.
  const [imapHost, setImapHost] = useState(account?.imap_host ?? "");
  const [imapPort, setImapPort] = useState<number | "">(account?.imap_port ?? DEFAULT_IMAP_PORT);
  const [imapSecurity, setImapSecurity] = useState<MailSecurity>(account?.imap_security ?? "ssl");
  const [mailPassword, setMailPassword] = useState("");
  // SMTP
  const [smtpHost, setSmtpHost] = useState(account?.smtp_host ?? "");
  const [smtpPort, setSmtpPort] = useState<number | "">(account?.smtp_port ?? DEFAULT_SMTP_PORT);
  const [smtpSecurity, setSmtpSecurity] = useState<MailSecurity>(account?.smtp_security ?? "starttls");

  const [pageError, setPageError] = useState<string | null>(null);
  const [imapTestResult, setImapTestResult] = useState<TestResultPanel | null>(null);
  const [smtpTestResult, setSmtpTestResult] = useState<TestResultPanel | null>(null);

  React.useEffect(() => {
    if (account) return;
    const detected = detectProviderFromEmail(emailAddress);
    setProviderKey(detected);
    const preset = presetToFormValues(detected);
    if (!preset) return;
    setImapHost(preset.imap.host);
    setImapPort(preset.imap.port);
    setImapSecurity(preset.imap.security as MailSecurity);
    setSmtpHost(preset.smtp.host);
    setSmtpPort(preset.smtp.port);
    setSmtpSecurity(preset.smtp.security as MailSecurity);
  }, [account, emailAddress]);

  const onProviderChange = (key: ProviderKey) => {
    setProviderKey(key);
    const preset = presetToFormValues(key);
    if (!preset) return; // "other" — ユーザー入力保持
    setImapHost(preset.imap.host);
    setImapPort(preset.imap.port);
    setImapSecurity(preset.imap.security as MailSecurity);
    setSmtpHost(preset.smtp.host);
    setSmtpPort(preset.smtp.port);
    setSmtpSecurity(preset.smtp.security as MailSecurity);
  };

  function buildImapConfig(): MailSecurityBlock {
    return {
      host: imapHost.trim(),
      port: typeof imapPort === "number" ? imapPort : DEFAULT_IMAP_PORT,
      security: imapSecurity,
      username: emailAddress.trim(),
      // backend は password:null = 既存値保持。undefined は触らない。
      password: mailPassword || null,
    };
  }

  function buildSmtpConfig(): MailSecurityBlock {
    return {
      host: smtpHost.trim(),
      port: typeof smtpPort === "number" ? smtpPort : DEFAULT_SMTP_PORT,
      security: smtpSecurity,
      username: emailAddress.trim(),
      password: mailPassword || null,
    };
  }

  async function handleSave() {
    setPageError(null);
    if (!name.trim()) {
      setPageError("名前は必須です");
      return;
    }
    if (!emailAddress.trim() || !emailAddress.includes("@")) {
      setPageError("メールアドレスの形式が正しくありません");
      return;
    }
    if (!imapHost.trim()) {
      setPageError("IMAP の host は必須です");
      return;
    }
    if (!smtpHost.trim()) {
      setPageError("SMTP の host は必須です");
      return;
    }
    if (!isEditing && !mailPassword) {
      setPageError("新規作成時はパスワードが必要です");
      return;
    }
    if (isDisconnected && !mailPassword) {
      setPageError("連携解除済みアカウントを再有効化するにはパスワードを再設定してください");
      return;
    }

    const body: MailAccountCreateBody = {
      name: name.trim(),
      email_address: emailAddress.trim(),
      imap: buildImapConfig(),
      smtp: buildSmtpConfig(),
      is_active: isActive,
      account_type: accountType,
    };
    try {
      await onSave(body);
      // success → password クリア(security 習慣)
      setMailPassword("");
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setPageError(msg);
    }
  }

  async function handleTestImap() {
    setPageError(null);
    setImapTestResult(null);
    if (!emailAddress.trim() || !emailAddress.includes("@")) {
      setPageError("メールアドレスの形式が正しくありません");
      return;
    }
    if (!imapHost.trim()) {
      setPageError("IMAP の host は必須です");
      return;
    }
    try {
      const result = await onTestImap({
        mode: "input",
        config: buildImapConfig(),
      });
      setImapTestResult({ result });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setImapTestResult({
        result: {
          status: "error",
          mode: "input",
          provider: "imap",
          message: `接続テストに失敗しました: ${msg}`,
        },
      });
    }
  }

  async function handleTestSmtp() {
    setPageError(null);
    setSmtpTestResult(null);
    if (!emailAddress.trim() || !emailAddress.includes("@")) {
      setPageError("メールアドレスの形式が正しくありません");
      return;
    }
    if (!smtpHost.trim()) {
      setPageError("SMTP の host は必須です");
      return;
    }
    try {
      const result = await onTestSmtp({
        mode: "input",
        config: buildSmtpConfig(),
      });
      setSmtpTestResult({ result });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setSmtpTestResult({
        result: {
          status: "error",
          mode: "input",
          provider: "smtp",
          message: `接続テストに失敗しました: ${msg}`,
        },
      });
    }
  }

  const portDisabled = busy !== null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
      <div
        style={{
          padding: "8px 12px",
          background: isDisconnected ? "#fef2f2" : "#f0f9ff",
          color: isDisconnected ? "#991b1b" : "#0c4a6e",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {isDisconnected
          ? "このアカウントは連携解除済みです。再有効化するにはパスワードを再設定してください。"
          : isEditing
            ? "既存アカウントを編集中"
            : "新規アカウントを追加します"}
      </div>

      {pageError && (
        <div
          style={{
            padding: "10px 12px",
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {pageError}
        </div>
      )}

      <div className="field">
        <label className="field-label">
          名前 <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          className="input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="サポート窓口"
          disabled={portDisabled}
        />
      </div>

      <div className="field">
        <label className="field-label">
          メールアドレス <span style={{ color: "#dc2626" }}>*</span>
        </label>
        <input
          className="input"
          type="email"
          value={emailAddress}
          onChange={(e) => setEmailAddress(e.target.value)}
          placeholder="support@example.jp"
          disabled={portDisabled}
        />
      </div>

      <SecretField
        label={`パスワード${isEditing ? " (変更する場合のみ入力)" : ""}`}
        value={mailPassword}
        onChange={setMailPassword}
        placeholder={isEditing ? "新規設定する場合のみ入力" : "アプリパスワード"}
        required={!isEditing || isDisconnected}
        disabled={portDisabled}
        hint={isEditing && !isDisconnected
          ? "IMAP / SMTP 共通で使用します。保存後は表示されません"
          : isDisconnected
            ? "連携解除後の再設定に必須です"
            : "IMAP / SMTP 共通で使用します。保存後は表示されません"}
      />

      <div className="field">
        <label className="field-label">アカウント種別</label>
        <select
          className="input"
          value={accountType}
          onChange={(e) => setAccountType(e.target.value as AccountType)}
          disabled={portDisabled}
        >
          {allowedAccountTypes.includes("personal") && <option value="personal">個人メール</option>}
          {allowedAccountTypes.includes("shared") && <option value="shared">共有メール</option>}
          {allowedAccountTypes.includes("other") && <option value="other">その他</option>}
        </select>
      </div>

      {!isEditing && (
        <div className="field">
          <label className="field-label">メールサービス</label>
          <select
            className="input"
            value={providerKey}
            onChange={(e) => onProviderChange(e.target.value as ProviderKey)}
            disabled={portDisabled}
          >
            {PROVIDER_ORDER.map((k) => (
              <option key={k} value={k}>{PROVIDER_LABELS[k]}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
            Gmail / Yahoo! JAPAN はメールアドレスから自動選択されます
          </div>
        </div>
      )}

      {/* IMAP section */}
      <div
        style={{
          borderTop: "1px dashed #e5e7eb",
          paddingTop: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151", fontWeight: 600, fontSize: 13 }}>
          <Server size={14} /> IMAP(受信)
        </div>
        <div className="field">
          <label className="field-label">
            Host <span style={{ color: "#dc2626" }}>*</span>
          </label>
          <input
            className="input"
            type="text"
            value={imapHost}
            onChange={(e) => setImapHost(e.target.value)}
            placeholder="imap.example.com"
            disabled={portDisabled}
          />
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">
              Port <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              className="input"
              type="number"
              value={imapPort}
              onChange={(e) => setImapPort(e.target.value === "" ? "" : Number(e.target.value))}
              placeholder={String(DEFAULT_IMAP_PORT)}
              disabled={portDisabled}
            />
          </div>

          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Security</label>
            <select
              className="input"
              value={imapSecurity}
              onChange={(e) => setImapSecurity(e.target.value as MailSecurity)}
              disabled={portDisabled}
            >
              <option value="ssl">ssl</option>
              <option value="starttls">starttls</option>
              <option value="none">none</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: 4 }}>
          <Button
            variant="secondary"
            onClick={handleTestImap}
            disabled={busy !== null}
          >
            IMAP 接続テスト
          </Button>
        </div>
        {imapTestResult && <ConnectionTestPanel label="IMAP" result={imapTestResult.result} />}
      </div>

      {/* SMTP section */}
      <div
        style={{
          borderTop: "1px dashed #e5e7eb",
          paddingTop: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#374151", fontWeight: 600, fontSize: 13 }}>
          <Mail size={14} /> SMTP(送信)
        </div>
        <div className="field">
          <label className="field-label">
            Host <span style={{ color: "#dc2626" }}>*</span>
          </label>
          <input
            className="input"
            type="text"
            value={smtpHost}
            onChange={(e) => setSmtpHost(e.target.value)}
            placeholder="smtp.example.com"
            disabled={portDisabled}
          />
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">
              Port <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              className="input"
              type="number"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value === "" ? "" : Number(e.target.value))}
              placeholder={String(DEFAULT_SMTP_PORT)}
              disabled={portDisabled}
            />
          </div>

          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Security</label>
            <select
              className="input"
              value={smtpSecurity}
              onChange={(e) => setSmtpSecurity(e.target.value as MailSecurity)}
              disabled={portDisabled}
            >
              <option value="ssl">ssl</option>
              <option value="starttls">starttls</option>
              <option value="none">none</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: 4 }}>
          <Button
            variant="secondary"
            onClick={handleTestSmtp}
            disabled={busy !== null}
          >
            SMTP 接続テスト
          </Button>
        </div>
        {smtpTestResult && <ConnectionTestPanel label="SMTP" result={smtpTestResult.result} />}
      </div>

      <div className="field" style={{ marginTop: 4 }}>
        <label
          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151" }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            disabled={portDisabled}
          />
          有効 (is_active)
        </label>
        <span className="field-hint">
          オフにしても DB 行と履歴は残ります(連携解除相当)。
        </span>
      </div>

      <hr className="divider" />

      <div className="row gap-8">
        <Button
          iconLeft={<Save size={13} />}
          onClick={handleSave}
          disabled={busy !== null}
        >
          保存
        </Button>
        <Button
          variant="secondary"
          iconLeft={<RefreshCw size={13} />}
          onClick={onCancel}
          disabled={busy !== null}
        >
          キャンセル
        </Button>
      </div>
    </div>
  );
}

function ConnectionTestPanel({ label, result }: { label: string; result: MailTestResult }) {
  const ok = result.status === "connected";
  return (
    <div
      style={{
        marginTop: 10,
        padding: "10px 12px",
        background: ok ? "#f0fdf4" : "#fef2f2",
        border: `1px solid ${ok ? "#bbf7d0" : "#fecaca"}`,
        borderRadius: 8,
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        fontSize: 13,
      }}
      role="status"
    >
      <span
        style={{
          fontWeight: 700,
          color: ok ? "#16a34a" : "#dc2626",
          flexShrink: 0,
        }}
      >
        {ok ? "✓" : "✗"}
      </span>
      <div style={{ flex: 1 }}>
        <div className="row gap-8" style={{ marginBottom: 4 }}>
          <strong style={{ color: ok ? "#166534" : "#991b1b" }}>
            {label} {result.status}
          </strong>
          <span style={{ color: "#6b7280", fontSize: 11 }}>
            mode={result.mode}
          </span>
        </div>
        <div style={{ color: "#374151" }}>{result.message}</div>
      </div>
    </div>
  );
}
